import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Content, RightActions } from '../../../components/layout/content';
import * as Page from '../../../components/content/page-header';
import { Icons } from '../../../components/icons/icons';
import * as Button from '../../../components/button/button';
import {
  useDeleteMutation,
  usePostMutation,
  usePutMutation
} from '../../../hooks/use-mutations';
import { Actions, Content as DialogContent, Dialog, Title } from '../../../components/dialog/dialog';
import { Label, TextInputLarge } from '../../../components/forms/input';
import { FileInputLarge } from '../../../components/forms/file';
import { FieldValues, useForm } from 'react-hook-form';
import { HazardType, HazardTypeLayer } from './types';
import { useQuery } from '../../../hooks/use-query';
import { Modes } from '../../../types/page-modes';
import usePageState from '../../../hooks/use-page-state';
import { SortableTable } from '../../../components/table/sortable-table';
import { LayerFilter } from '../../../widgets/filter-layers';
import { usePutFormDataMutation } from '../../../hooks/use-form-mutations';

export const HazardTypeEditor = (): JSX.Element => {
  const [pageState, setPageState] = usePageState();
  const navigate = useNavigate();
  const params = useParams();
  const id = params.id as string;
  const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm();
  const uploadIcon = watch('uploadIcon');

  const inputRef = useRef<HTMLInputElement>();
  const hazardType = useQuery<HazardType>({ path: `/hazard-types/${id}` });
  const hazardLayers = useQuery<HazardTypeLayer[]>({ path: `/hazard-types/${id}/layers` });
  const putMutation = usePutFormDataMutation({ path: `/hazard-types/${id}` });
  const addLayerMutation = usePostMutation<HazardTypeLayer>({ path: `/hazard-types/${id}/layers` });
  const deleteLayerMutation = useDeleteMutation<HazardTypeLayer>({ cacheKey: `/hazard-types/${id}/layers` });
  const putMutationReorderLayers = usePutMutation({ path: `/hazard-types/${id}/layers/reorder` });
  const [rows, setRows] = useState<HazardTypeLayer[]>([]);
  const [selectedLayers, setSelectedLayers] = useState<string[]>([]);

  useEffect(() => {
    if (hazardType.isSuccess) {
      setExistingData(hazardType.data);
    }
  }, [hazardType.isSuccess]);

  useEffect(() => {
    if (putMutation.isSuccess) {
      navigate('/settings/hazard-types');
    }
  }, [putMutation.isSuccess]);

  useEffect(() => {
    if (hazardLayers.isSuccess) {
      setSelectedLayers(hazardLayers.data.map(item => item.layerId as string));
    }
  }, [hazardLayers.isSuccess]);

  const setExistingData = (item: HazardType): void => {
    for (const key in item) {
      setValue(key, item[key as keyof HazardType]);
    }
  };

  const triggerUpload = (): void => {
    inputRef.current?.click?.();
  };

  const onSubmit = (data: FieldValues): void => {
    putMutation.mutate(data);
  };

  const onFileChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setValue('uploadIcon', event.target.files);
  };

  const onAdd = (item: HazardTypeLayer): void => {
    setSelectedLayers([...selectedLayers, item.id]);
    addLayerMutation.mutate({ layerId: item.id });
  };

  const onDeleteConfirm = (): void => {
    setSelectedLayers(selectedLayers.filter(layerId => layerId !== pageState.item?.layerId));
    deleteLayerMutation.mutate({ path: `/hazard-types/${id}/layers/${pageState.item?.id as string}` });
    setPageState(Modes.BROWSE);
  };

  const existingLayersColumns = [
    { accessor: '', Header: 'Sort' },
    { accessor: 'name', Header: 'Name' },
    { accessor: 'type', Header: 'Type' },
  ];

  const onDragEnd = (_: string, data: HazardTypeLayer[]): void => {
    setRows(data);
    putMutationReorderLayers.mutate(data.map(item => item.id));
  };

  return <>
    <RightActions>
      <Button.Primary type="submit" form="update-form" disabled={putMutation.isLoading}>
        Update
      </Button.Primary>
      <Button.Tertiary disabled={putMutation.isLoading} onClick={(): void => navigate('/settings/hazard-types')}>
        Cancel
      </Button.Tertiary>
    </RightActions>
    <Content>
      <Page.Header>
        <Page.Title>Hazard Types</Page.Title>
        <Page.SubTitle>
          Hazard Types in RAPIDS.
        </Page.SubTitle>
      </Page.Header>
      <form id="update-form" onSubmit={handleSubmit(onSubmit)}>
        <Title>Edit Hazard Type</Title>
        <div className="mb-4">
          <Label name="slug">Hazard Type Id</Label>
          <TextInputLarge {...register('slug', { required: 'You must provide a value for Hazard Type Id.' })} errors={errors} placeholder="Hazard Type Id" />
        </div>
        <div className="mb-4">
          <Label name="name">Hazard Type Name</Label>
          <TextInputLarge {...register('name', { required: 'You must provide a value for Hazard Type Name.' })} errors={errors} placeholder="Hazard Type Name" />
        </div>
        {!uploadIcon ? <div className="mb-4">
          <Label className="offset-label-icon">Hazard Type Url</Label>
          <div className="d-flex" onClick={triggerUpload}>
            <div className="icon-wrapper">
              <img src={hazardType.data?.iconUrl} width={29} height={29} />
            </div>
            <div className="text-input">
              {hazardType.data?.iconUrl}
            </div>
          </div>
        </div> : <span />}
        <div className="mb-4" style={{ display: uploadIcon ? '' : 'none' }}>
          <Label>Hazard Type Icon</Label>
          <FileInputLarge { ...register('uploadIcon', { required: Boolean(uploadIcon) && 'You must provide a value for Hazard Type Icon.' })} errors={errors} accept="image/png" ref={inputRef} onChange={onFileChange} />
          <span className="supported-file-format">Supported image format: PNG</span>
        </div>
      </form>
      <div className="mb-3 mt-3 d-flex justify-content-between">
        <span />
        <Button.LightBlackSmall onClick={(): void => setPageState(Modes.CREATE)}>
          <Icons.Add />
          Add Layer
        </Button.LightBlackSmall>
      </div>
      <SortableTable
        columns={existingLayersColumns}
        rows={rows.length ? rows : hazardLayers.data || []}
        onDragEnd={onDragEnd}
        onDelete={(item): void => setPageState(Modes.DELETE, item)}
      />
    </Content>
    {pageState.mode === Modes.DELETE && (
      <Dialog onCancel={(): void => setPageState(Modes.BROWSE)} backdropClose={false}>
        <Title>Delete Hazard Type Layer</Title>
        <DialogContent>Are you sure you want to delete?</DialogContent>
        <Actions>
          <Button.Tertiary onClick={(): void => setPageState(Modes.BROWSE)}>Cancel</Button.Tertiary>
          <Button.Danger onClick={onDeleteConfirm}>Delete</Button.Danger>
        </Actions>
      </Dialog>
    )}
    {pageState.mode === Modes.CREATE && (
      <LayerFilter
        title="Add Layer Into Hazard Type"
        onAdd={onAdd}
        selectedLayers={selectedLayers}
        onClose={(): void => setPageState(Modes.BROWSE)}
      />
    )}
  </>;
};
